import { useState, useRef } from "react";
import { useTranslation } from "react-i18next";

import api from "@helpers/api";

import CustomModal from "@components/CustomModal";

import ReorderPanel from "../_shared/ReorderPanel";

// API responses are sometimes the entity directly, sometimes wrapped in { data }.
function unwrap(res) {
    const body = res?.data;
    return body?.data ?? body;
}

/**
 * Drag-to-reorder the contents inside a playlist.
 *
 * Open with the playlist id; the modal fetches the playlist (and its contents)
 * and persists the new order via PATCH /content/order.
 *
 *   modalRef.current.open({ playlistId }, onSaved)
 */
export default function ReorderContentsModal({ modalRef }) {
    const { t } = useTranslation("contentModals");
    const [open, setOpen] = useState(false);
    const [saving, setSaving] = useState(false);
    const [fetching, setFetching] = useState(false);
    const [items, setItems] = useState([]);
    const [errorTitle, setErrorTitle] = useState(null);
    const [errorMessage, setErrorMessage] = useState(null);

    const REF_playlistId = useRef(null);
    const REF_sectionId = useRef(null);
    const REF_callback = useRef();
    const REF_initialOrder = useRef([]);

    modalRef.current = {
        open(config, callback) {
            REF_playlistId.current = config.playlistId ?? config.id;
            REF_sectionId.current = config.sectionId ?? config.section ?? null;
            REF_callback.current = callback;
            setItems([]);
            setErrorTitle(null);
            setErrorMessage(null);
            setOpen(true);
            load();
        },
    };

    async function load() {
        setFetching(true);
        try {
            const playlist = unwrap(
                await api.get(`section/playlist/${REF_playlistId.current}`)
            );

            // Carry the section id from the response so the save payload stays
            // backward compatible with the current ReorderContent request.
            if (playlist?.section != null) REF_sectionId.current = playlist.section;

            const contents = (playlist?.contents ?? []).map((content) => ({
                id: content.id,
                name: content.name,
                banner: content.banner,
            }));

            setItems(contents);
            REF_initialOrder.current = contents.map((content) => content.id);
        } catch (e) {
            console.error("Failed to load contents for reorder:", e);
            setErrorTitle(t("reorderContents.loadErrorTitle"));
            setErrorMessage(t("reorderContents.loadErrorMessage"));
        }
        setFetching(false);
    }

    async function handleSubmit() {
        try {
            setSaving(true);
            const contents = items.map((item, index) => ({
                id: item.id,
                order: index,
            }));

            await api.patch("content/order", {
                contents,
                // Currently still required by the backend request; harmless once
                // the endpoint drops it (see ClickUp 868jq94cx §3b).
                section: REF_sectionId.current,
            });

            await REF_callback.current?.();
            setOpen(false);
        } catch (e) {
            console.error("Failed to reorder contents:", e);
            setErrorTitle(t("reorderContents.saveErrorTitle"));
            setErrorMessage(t("reorderContents.saveErrorMessage"));
        }
        setSaving(false);
    }

    const isDirty = items.some(
        (item, index) => item.id !== REF_initialOrder.current[index]
    );

    return (
        <CustomModal
            maxWidth={460}
            title={t("reorderContents.modalTitle")}
            bottomButtonSumbitText={t("reorderContents.submitButton")}
            bottomButtonCancelText={t("common:actions.cancel")}
            loading={saving || fetching}
            disabled={!isDirty || items.length === 0}
            open={open}
            handleClose={() => setOpen(false)}
            handleSubmit={handleSubmit}
            errorTitle={errorTitle}
            errorMessage={errorMessage}
            bodyComponent={
                open ? (
                    <ReorderPanel
                        subtitle={t("reorderContents.subtitle")}
                        loading={fetching}
                        items={items}
                        onReorder={setItems}
                        emptyLabel={t("reorderContents.emptyLabel")}
                    />
                ) : null
            }
        />
    );
}
